// MemoryMonitorService - Real-time memory monitoring for upload operations
// Phase 3B: Performance Optimization & Monitoring

// Extend MemoryInfo to include optional external memory property
interface ExtendedMemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  external?: number;
}

interface MemoryThresholds {
  warning: number; // MB
  critical: number; // MB
  maximum: number; // MB
}

interface MemoryStatus {
  heapUsed: number; // MB
  heapTotal: number; // MB
  heapLimit: number; // MB
  external: number; // MB
  usagePercent: number; // 0-100
  availableMemory: number; // MB
  status: "safe" | "warning" | "critical" | "danger";
  recommendation: string;
  canStartUpload: boolean;
  suggestedChunkSize?: number;
}

interface MemoryAlert {
  level: "warning" | "critical" | "danger";
  timestamp: number;
  memoryUsage: number;
  message: string;
  recommendation: string;
}

// type MemoryEventType = "threshold-exceeded" | "memory-freed" | "gc-recommended";
type MemoryEventCallback = (alert: MemoryAlert) => void;

class MemoryMonitorService {
  private readonly thresholds: MemoryThresholds = {
    warning: 500, // 500MB - Allow more headroom for development
    critical: 750, // 750MB - Still provides warning before critical
    maximum: 1000, // 1GB - Modern browsers easily handle this
  };

  private eventListeners: MemoryEventCallback[] = [];
  private monitorInterval: NodeJS.Timeout | null = null;
  private lastStatus: MemoryStatus | null = null;
  private alertHistory: MemoryAlert[] = [];
  private readonly maxAlertHistory = 20;
  private readonly monitorIntervalMs = 2000; // Check every 2 seconds
  private isMonitoring = false;

  constructor(customThresholds?: Partial<MemoryThresholds>) {
    if (customThresholds) {
      this.thresholds = { ...this.thresholds, ...customThresholds };
    }
  }

  /**
   * Start memory monitoring
   */
  startMonitoring(): void {
    if (this.isMonitoring) return;

    this.isMonitoring = true;
    this.monitorInterval = setInterval(() => {
      this.checkMemoryStatus();
    }, this.monitorIntervalMs);
  }

  /**
   * Stop memory monitoring
   */
  stopMonitoring(): void {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
      this.monitorInterval = null;
    }
    this.isMonitoring = false;
  }

  /**
   * Get current memory status
   */
  getCurrentMemoryStatus(): MemoryStatus {
    if (!this.isMemoryAPIAvailable()) {
      return this.createFallbackStatus();
    }

    const memory = performance.memory;
    const heapUsed = Math.round(memory.usedJSHeapSize / 1024 / 1024);
    const heapTotal = Math.round(memory.totalJSHeapSize / 1024 / 1024);
    const heapLimit = Math.round(memory.jsHeapSizeLimit / 1024 / 1024);
    const external = Math.round(
      ((memory as ExtendedMemoryInfo).external || 0) / 1024 / 1024,
    );

    const usagePercent = (heapUsed / heapLimit) * 100;
    const availableMemory = heapLimit - heapUsed;

    // Determine status
    let status: MemoryStatus["status"] = "safe";
    let recommendation = "Memory usage is within safe limits";
    let canStartUpload = true;
    let suggestedChunkSize: number | undefined;

    if (heapUsed >= this.thresholds.maximum) {
      status = "danger";
      recommendation = "Critical memory usage! Stop all uploads immediately";
      canStartUpload = false;
    } else if (heapUsed >= this.thresholds.critical) {
      status = "critical";
      recommendation =
        "High memory usage. Use smaller chunk sizes and limit concurrent operations";
      canStartUpload = true;
      suggestedChunkSize = 1 * 1024 * 1024; // 1MB chunks
    } else if (heapUsed >= this.thresholds.warning) {
      status = "warning";
      recommendation =
        "Elevated memory usage. Monitor closely and consider reducing chunk sizes";
      canStartUpload = true;
      suggestedChunkSize = 5 * 1024 * 1024; // 5MB chunks
    } else {
      suggestedChunkSize = 10 * 1024 * 1024; // 10MB chunks (default)
    }

    return {
      heapUsed,
      heapTotal,
      heapLimit,
      external,
      usagePercent,
      availableMemory,
      status,
      recommendation,
      canStartUpload,
      suggestedChunkSize,
    };
  }

  /**
   * Check memory status and emit alerts if needed
   */
  private checkMemoryStatus(): void {
    const currentStatus = this.getCurrentMemoryStatus();

    // Check for status changes that warrant alerts
    if (this.lastStatus) {
      // Alert on threshold crossings
      if (this.shouldEmitAlert(this.lastStatus, currentStatus)) {
        this.emitMemoryAlert(currentStatus);
      }

      // Recommend garbage collection if memory freed significantly
      if (this.lastStatus.heapUsed - currentStatus.heapUsed > 20) {
        this.emitGCAlert(currentStatus, "Memory usage decreased significantly");
      }
    }

    this.lastStatus = currentStatus;
  }

  /**
   * Determine if an alert should be emitted
   */
  private shouldEmitAlert(
    lastStatus: MemoryStatus,
    currentStatus: MemoryStatus,
  ): boolean {
    // Alert on status level changes
    if (lastStatus.status !== currentStatus.status) {
      return true;
    }

    // Alert on significant memory increases
    const memoryIncrease = currentStatus.heapUsed - lastStatus.heapUsed;
    if (memoryIncrease > 30) {
      // 30MB increase
      return true;
    }

    return false;
  }

  /**
   * Emit memory alert
   */
  private emitMemoryAlert(status: MemoryStatus): void {
    let level: MemoryAlert["level"] = "warning";
    let message = "Memory usage has increased";

    switch (status.status) {
      case "danger":
        level = "critical";
        message = `Critical memory usage: ${status.heapUsed}MB (${status.usagePercent.toFixed(1)}%)`;
        break;
      case "critical":
        level = "critical";
        message = `High memory usage: ${status.heapUsed}MB (${status.usagePercent.toFixed(1)}%)`;
        break;
      case "warning":
        level = "warning";
        message = `Elevated memory usage: ${status.heapUsed}MB (${status.usagePercent.toFixed(1)}%)`;
        break;
    }

    const alert: MemoryAlert = {
      level,
      timestamp: Date.now(),
      memoryUsage: status.heapUsed,
      message,
      recommendation: status.recommendation,
    };

    this.addAlertToHistory(alert);
    this.notifyListeners(alert);
  }

  /**
   * Emit garbage collection recommendation alert
   */
  private emitGCAlert(status: MemoryStatus, reason: string): void {
    const alert: MemoryAlert = {
      level: "warning",
      timestamp: Date.now(),
      memoryUsage: status.heapUsed,
      message: `Garbage collection recommended: ${reason}`,
      recommendation: "Consider triggering cleanup operations",
    };

    this.addAlertToHistory(alert);
  }

  /**
   * Add alert to history
   */
  private addAlertToHistory(alert: MemoryAlert): void {
    this.alertHistory.push(alert);
    if (this.alertHistory.length > this.maxAlertHistory) {
      this.alertHistory.shift();
    }
  }

  /**
   * Notify all listeners of memory alert
   */
  private notifyListeners(alert: MemoryAlert): void {
    this.eventListeners.forEach((callback) => {
      try {
        callback(alert);
      } catch (error) {
        console.error("MemoryMonitorService: Error in alert callback", error);
      }
    });
  }

  /**
   * Check if memory API is available
   */
  private isMemoryAPIAvailable(): boolean {
    return (
      typeof performance !== "undefined" &&
      performance.memory !== undefined &&
      typeof performance.memory.usedJSHeapSize === "number"
    );
  }

  /**
   * Create fallback status when memory API unavailable
   */
  private createFallbackStatus(): MemoryStatus {
    return {
      heapUsed: 0,
      heapTotal: 0,
      heapLimit: 0,
      external: 0,
      usagePercent: 0,
      availableMemory: 0,
      status: "safe",
      recommendation: "Memory monitoring not available in this browser",
      canStartUpload: true,
      suggestedChunkSize: 10 * 1024 * 1024, // 10MB default
    };
  }

  /**
   * Add memory event listener
   */
  addEventListener(callback: MemoryEventCallback): () => void {
    this.eventListeners.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.eventListeners.indexOf(callback);
      if (index > -1) {
        this.eventListeners.splice(index, 1);
      }
    };
  }

  /**
   * Check if it's safe to start a new upload
   */
  canStartUpload(estimatedMemoryUsage?: number): {
    allowed: boolean;
    reason?: string;
    suggestedDelay?: number;
  } {
    const status = this.getCurrentMemoryStatus();

    if (!status.canStartUpload) {
      return {
        allowed: false,
        reason: "Memory usage too high to start new uploads",
      };
    }

    // Check estimated memory usage if provided
    if (estimatedMemoryUsage) {
      const estimatedTotalUsage =
        status.heapUsed + estimatedMemoryUsage / 1024 / 1024;

      if (estimatedTotalUsage > this.thresholds.critical) {
        return {
          allowed: false,
          reason: "Estimated memory usage would exceed critical threshold",
          suggestedDelay: 30000, // Wait 30 seconds
        };
      }

      if (estimatedTotalUsage > this.thresholds.warning) {
        return {
          allowed: true,
          reason: "Upload allowed but will approach memory limits",
        };
      }
    }

    return { allowed: true };
  }

  /**
   * Get recommended chunk size based on current memory status
   */
  getRecommendedChunkSize(): number {
    const status = this.getCurrentMemoryStatus();
    return status.suggestedChunkSize || 10 * 1024 * 1024;
  }

  /**
   * Get recent alert history
   */
  getAlertHistory(count: number = 10): MemoryAlert[] {
    return this.alertHistory.slice(-count);
  }

  /**
   * Get memory usage trend (last N checks)
   */
  getMemoryTrend(): { timestamp: number; usage: number }[] {
    // This would typically be stored from periodic checks
    // For now, return current status
    const current = this.getCurrentMemoryStatus();
    return [
      {
        timestamp: Date.now(),
        usage: current.heapUsed,
      },
    ];
  }

  /**
   * Force memory status check (useful for immediate updates)
   */
  forceCheck(): MemoryStatus {
    const status = this.getCurrentMemoryStatus();

    if (this.lastStatus && this.shouldEmitAlert(this.lastStatus, status)) {
      this.emitMemoryAlert(status);
    }

    this.lastStatus = status;
    return status;
  }

  /**
   * Update memory thresholds
   */
  updateThresholds(newThresholds: Partial<MemoryThresholds>): void {
    Object.assign(this.thresholds, newThresholds);
  }

  /**
   * Get current thresholds
   */
  getThresholds(): MemoryThresholds {
    return { ...this.thresholds };
  }

  /**
   * Clear alert history
   */
  clearAlertHistory(): void {
    this.alertHistory = [];
  }

  /**
   * Get memory monitoring statistics
   */
  getMonitoringStats() {
    const current = this.getCurrentMemoryStatus();
    const recentAlerts = this.getAlertHistory(5);

    return {
      isMonitoring: this.isMonitoring,
      memoryAPIAvailable: this.isMemoryAPIAvailable(),
      currentStatus: current,
      thresholds: this.thresholds,
      totalAlerts: this.alertHistory.length,
      recentAlerts,
      lastCheck: this.lastStatus?.heapUsed ? Date.now() : null,
    };
  }

  /**
   * Cleanup on service destruction
   */
  destroy(): void {
    this.stopMonitoring();
    this.eventListeners = [];
    this.alertHistory = [];
  }
}

// Export singleton instance
export const memoryMonitorService = new MemoryMonitorService();

export type {
  MemoryStatus,
  MemoryAlert,
  MemoryThresholds,
  MemoryEventCallback,
};
